import { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'

import { AppFooter } from '@/components/layout/AppFooter'
import { AppHeader } from '@/components/layout/AppHeader'
import { AppSidebar } from '@/components/layout/AppSidebar'
import { useNotificationSocket } from '@/hooks/useNotificationSocket'
import { useAuthStore } from '@/store/authStore'

const THEME_MODE_STORAGE_KEY = 'job-portal.theme-mode'

function getInitialThemeMode(): 'light' | 'dark' {
  const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY)
  return stored === 'dark' ? 'dark' : 'light'
}

/**
 * The Zynix theme drives its CSS entirely off data-* attributes on <html>
 * (see styles/zynix/_switcher.scss), not classes — this layout is the single
 * place that owns those attributes for the authenticated app shell.
 */
export function AppShellLayout() {
  const user = useAuthStore((s) => s.user)
  // sidebarCollapsed=true means the sidebar is hidden (desktop: docked panel
  // slid away, content reclaims its width; mobile: drawer off-screen).
  // Defaulting to false unconditionally meant the drawer was open on first
  // paint on mobile, blocking the page underneath — start it collapsed below
  // the desktop breakpoint so mobile loads with the drawer closed instead.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 992,
  )
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>(getInitialThemeMode)

  useNotificationSocket()

  // Below the desktop breakpoint the sidebar is a drawer, not a docked
  // panel — collapse it when a nav link is clicked so the destination page
  // is visible instead of staying hidden behind the still-open drawer.
  const collapseSidebarOnMobile = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 992) {
      setSidebarCollapsed(true)
    }
  }

  useEffect(() => {
    const html = document.documentElement
    html.setAttribute('data-nav-layout', 'vertical')
    html.setAttribute('data-width', 'fullwidth')

    return () => {
      html.removeAttribute('data-nav-layout')
      html.removeAttribute('data-width')
      html.removeAttribute('data-header-styles')
      html.removeAttribute('data-menu-styles')
      html.removeAttribute('data-toggled')
    }
  }, [])

  useEffect(() => {
    // Zynix's own [data-toggled="close"/"open"] rules (_switcher.scss) exist
    // for both mobile and — via a hand-added desktop override in theme.scss
    // — desktop widths too, but neither reliably applies: the selector
    // compiles correctly and matches the DOM (confirmed via
    // document.querySelector + getComputedStyle introspection) yet the
    // computed transform never reflects it, on mobile just as much as
    // desktop. Root cause was never pinned down (possibly a cascade-layers
    // interaction in the compiled theme bundle). Inline styles — which
    // nothing in an external stylesheet can outrank — are the reliable
    // option, so the toggle is driven directly here at every viewport size,
    // with data-toggled still set for markup correctness. A resize listener
    // keeps it correct if the viewport crosses the 992px breakpoint without
    // a reload (e.g. rotating a tablet).
    const html = document.documentElement
    const pageEl = document.querySelector<HTMLElement>('.page')
    const sidebarEl = document.querySelector<HTMLElement>('.app-sidebar')
    const contentEl = document.querySelector<HTMLElement>('.app-content')
    const overlayEl = document.querySelector<HTMLElement>('#responsive-overlay')

    // Zynix's menu-styles/vertical.scss also has, unconditionally on
    // desktop: [data-toggled="open"] .page { position: absolute; } with no
    // accompanying inset/width. That's our normal default state (sidebar
    // visible), so .page silently shrinks to its content width instead of
    // the viewport, leaving a gap on the right — and the gap disappears
    // when the sidebar is toggled closed (data-toggled flips away from
    // "open"), which reads as the toggle "breaking" the layout rather than
    // fixing it. Pin .page back to normal flow at every viewport size.
    pageEl?.style.setProperty('position', 'static', 'important')

    const applyLayout = () => {
      const isMobile = window.innerWidth < 992
      html.setAttribute('data-toggled', sidebarCollapsed ? 'close' : 'open')

      sidebarEl?.style.setProperty(
        'transform',
        sidebarCollapsed ? 'translateX(-15rem)' : 'translateX(0)',
        'important',
      )

      if (!isMobile && sidebarCollapsed) {
        contentEl?.style.setProperty('margin-inline-start', '0', 'important')
      } else {
        contentEl?.style.removeProperty('margin-inline-start')
      }

      // On mobile the sidebar is a fixed drawer, not a docked panel — Zynix
      // ships a #responsive-overlay element for exactly this (dim the page
      // behind an open drawer, tap it to close), but nothing here was ever
      // toggling it, so opening the menu just showed the drawer on top of
      // fully visible, still-scrollable page content instead of the
      // expected dimmed backdrop. Its compiled `&.active { visibility:
      // visible }` rule has the same unresolved cascade issue as the
      // transform/background rules above (class is verifiably applied, but
      // computed visibility doesn't follow) — set it inline too.
      const showOverlay = isMobile && !sidebarCollapsed
      overlayEl?.classList.toggle('active', showOverlay)
      overlayEl?.style.setProperty('visibility', showOverlay ? 'visible' : 'hidden', 'important')
      overlayEl?.style.setProperty('opacity', showOverlay ? '1' : '0', 'important')
      document.body.style.overflow = showOverlay ? 'hidden' : ''
    }

    applyLayout()
    window.addEventListener('resize', applyLayout)
    return () => window.removeEventListener('resize', applyLayout)
  }, [sidebarCollapsed])

  useEffect(() => {
    const html = document.documentElement
    // data-header-styles/data-menu-styles are the Zynix theme's own switches
    // for the header/sidebar color scheme — independent of data-theme-mode,
    // which only drives the main content area. Keep all three in lockstep so
    // toggling dark mode darkens the header and sidebar too.
    html.setAttribute('data-theme-mode', themeMode)
    html.setAttribute('data-header-styles', themeMode)
    html.setAttribute('data-menu-styles', themeMode)
    localStorage.setItem(THEME_MODE_STORAGE_KEY, themeMode)

    // .app-header/.app-sidebar's `background: var(--header-bg)` / `var(--menu-bg)`
    // rules verifiably resolve the CSS variable to the correct dark value
    // (confirmed via getComputedStyle on the elements) but the browser still
    // paints them white — the same unresolved cascade-layers interaction in
    // the compiled Zynix bundle already worked around above for the sidebar
    // toggle transform. Inline styles are the reliable fix here too.
    const headerEl = document.querySelector<HTMLElement>('.app-header')
    const sidebarEl = document.querySelector<HTMLElement>('.app-sidebar')
    const [bg, border] =
      themeMode === 'dark'
        ? ['rgb(35, 34, 38)', 'rgba(255, 255, 255, 0.1)']
        : ['#fff', '#f3f2f9']
    headerEl?.style.setProperty('background', bg, 'important')
    headerEl?.style.setProperty('border-block-end-color', border, 'important')
    sidebarEl?.style.setProperty('background', bg, 'important')
    sidebarEl?.style.setProperty('border-inline-end-color', border, 'important')
  }, [themeMode])

  if (!user) {
    return null
  }

  return (
    <div className="page">
      <AppHeader
        user={user}
        onToggleSidebar={() => setSidebarCollapsed((collapsed) => !collapsed)}
        onToggleThemeMode={() => setThemeMode((mode) => (mode === 'light' ? 'dark' : 'light'))}
        isDarkMode={themeMode === 'dark'}
      />
      <AppSidebar role={user.userType} onNavigate={collapseSidebarOnMobile} />
      <div
        id="responsive-overlay"
        onClick={() => setSidebarCollapsed(true)}
        aria-hidden="true"
      />
      <div className="main-content app-content">
        <div className="container-fluid">
          <Outlet />
        </div>
      </div>
      <AppFooter />
    </div>
  )
}
